// Global Instructions Rule Applied!
//
// Configuration → Internal Agents services: the aggregate admin read, the usage
// read, and the writes over cfg.app_config (group 'internal_agents'),
// cfg.internal_agent_types, cfg.internal_agent_task_models and cfg.llm_models.
//
// Same fetch conventions as documentConfigService. Every route is admin-gated
// at the backend AND inside the RPC, so a 403 surfaces as the readable
// "Administrator access required" from the body. A disable/delete of a model
// that is still routed comes back 409 with the count in the message — that is a
// designed outcome, not an error, and the panel shows it as a toast.
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  AgentConfigValueKey,
  InternalAgentAppConfigEntry,
  InternalAgentTypeAdminRow,
  InternalAgentTaskModelAdminRow,
  InternalAgentsConfigAdminData,
  InternalAgentsUsageData,
  LlmModelAdminRow,
  LpStatusCadenceRow,
  validateInternalAgentAppConfigEntry,
  validateLpStatusCadenceRow,
  validateInternalAgentTypeAdminRow,
  validateInternalAgentTaskModelAdminRow,
  validateLlmModelAdminRow
} from '../../../state_manager/data_models/internal_agents_config_model.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

// Writes use the house request envelope { data: {...} } — the /config/* routes
// unwrap it via _json_body, and a flat body is silently dropped (#232).
async function readJson(response: Response): Promise<any> {
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    // The backend's error envelope key is `message` (handle_route_custom_error);
    // `error` is the auth guard's. Reading `message` FIRST is what makes the
    // 403 and the 409 in-use refusal surface readably (#232).
    if (response.status === 401) throw new Error('Authentication failed - please log in again')
    throw new Error(body.message || body.error || `Request failed (${response.status})`)
  }
  return body
}

export const fetchInternalAgentsConfig = async (
  accessToken: string | null
): Promise<InternalAgentsConfigAdminData> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.INTERNAL_AGENTS_CONFIG),
    { method: API_ROUTES.CONFIG.INTERNAL_AGENTS_CONFIG.method, headers: authHeaders(accessToken) }
  )
  const body = await readJson(response)
  const data = body.data || {}
  const appConfig = (data.app_config || []) as InternalAgentAppConfigEntry[]
  const lpStatusCadence = (data.lp_status_cadence || []) as LpStatusCadenceRow[]
  const agentTypes = (data.agent_types || []) as InternalAgentTypeAdminRow[]
  const taskModels = (data.task_models || []) as InternalAgentTaskModelAdminRow[]
  const models = (data.llm_models || []) as LlmModelAdminRow[]
  validateInternalAgentAppConfigEntry(appConfig, { isArray: true })
  validateLpStatusCadenceRow(lpStatusCadence, { isArray: true })
  validateInternalAgentTypeAdminRow(agentTypes, { isArray: true })
  validateInternalAgentTaskModelAdminRow(taskModels, { isArray: true })
  validateLlmModelAdminRow(models, { isArray: true })
  const options = data.options || {}
  return {
    resolvedEnvironment: data.resolved_environment || 'global',
    appConfig,
    lpStatusCadence,
    agentTypes,
    taskModels,
    models,
    taskKeys: (options.task_keys || []) as string[],
    providers: (options.providers || []) as string[],
    taskParamKeys: (options.task_param_keys || []) as AgentConfigValueKey[]
  }
}

export const fetchInternalAgentsUsage = async (
  accessToken: string | null,
  days: number = 1
): Promise<InternalAgentsUsageData> => {
  // days = 1 is "since UTC midnight" (the daily-cap boundary); a wider
  // window is the Phase 5 cost report.
  const url = `${buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.INTERNAL_AGENTS_USAGE)}?days=${Math.max(1, Math.floor(days))}`
  const response = await fetch(
    url,
    { method: API_ROUTES.CONFIG.INTERNAL_AGENTS_USAGE.method, headers: authHeaders(accessToken) }
  )
  const body = await readJson(response)
  const data = body.data || {}
  return {
    since_utc: data.since_utc,
    window_days: data.window_days ?? 1,
    totals: data.totals || {},
    by_agent_type: data.by_agent_type || [],
    by_model: data.by_model || [],
    by_agent_type_task: data.by_agent_type_task || [],
    by_run_mode: data.by_run_mode || [],
    by_subject: data.by_subject || [],
    by_day: data.by_day || []
  }
}

/** One LP status's cadence policy. Send `rm_touchpoint_due_days: null`
 *  EXPLICITLY to mean "never chased" — the key's presence is what tells the
 *  backend to clear it rather than leave it unchanged. */
export interface LpStatusCadenceFields {
  rm_touchpoint_due_days?: number | null
  rm_cadence_draft_mode?: 'draft' | 'signal'
  agent_priority?: number
}

export const updateLpStatusCadence = async (
  accessToken: string | null,
  statusId: string,
  fields: LpStatusCadenceFields
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.INTERNAL_AGENT_LP_STATUS_CADENCE_UPDATE, {
      statusId
    }),
    {
      method: API_ROUTES.CONFIG.INTERNAL_AGENT_LP_STATUS_CADENCE_UPDATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

/** One global budget knob. `value` is the raw jsonb value — boolean for
 *  `enabled`, number for the numeric knobs, string for the signal-only date. */
export const updateInternalAgentsBudget = async (
  accessToken: string | null,
  configKey: string,
  value: boolean | number | string | string[]
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.INTERNAL_AGENTS_APP_CONFIG_UPDATE, {
      configKey
    }),
    {
      method: API_ROUTES.CONFIG.INTERNAL_AGENTS_APP_CONFIG_UPDATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: { value } })
    }
  )
  await readJson(response)
}

export interface InternalAgentTypeFields {
  max_cost_per_run_usd?: number | null
  max_runs_per_day?: number | null
  memory_token_budget?: number | null
  enabled?: boolean
  auto_backfill_on_provision?: boolean
  /** 0 = uncapped. */
  auto_backfill_max_per_hour?: number | null
  /** Days of continued following after the subject loses the auto-provision
   *  role. Send `null` EXPLICITLY to mean "follow forever" — the key's presence
   *  is what tells the backend to clear it rather than leave it unchanged, so
   *  never spread this field in with an undefined value. */
  unfollow_lag_days?: number | null
  /** cfg.pipeline_statuses ids whose stages do NOT count as pipeline
   *  membership (the follower skips deals parked at these decisions). Same
   *  presence convention as unfollow_lag_days: explicit `null` clears the
   *  filter ("no exclusions"), omitted leaves it unchanged. */
  follow_exclude_decision_statuses?: string[] | null
  /** Schedule for the pg_cron enqueue. 'none' disables scheduled runs. */
  schedule_frequency_unit?: 'none' | 'daily' | 'weekly' | 'monthly'
  /** Every N units — 2 with 'weekly' is fortnightly. */
  schedule_interval_count?: number
  /** ISO date (YYYY-MM-DD) the recurrence counts from: its WEEKDAY is the day a
   *  weekly schedule fires, its day-of-month for a monthly one. Same presence
   *  convention as unfollow_lag_days — explicit `null` clears it back to the
   *  type's creation date, omitted leaves it unchanged. */
  schedule_anchor_date?: string | null
  /** Minutes an interaction-triggered run waits for the burst to settle
   *  (1–1440). Omitted leaves it unchanged; there is no clear — the enqueue
   *  falls back to 30 for a NULL column. */
  debounce_minutes?: number
  /** Backfill depth policy (design §8.A). Months of history a first-time
   *  follower reads when it averaged ≥ the threshold interactions/month over
   *  the last year (1–240). */
  backfill_lookback_months_frequent?: number
  /** … and when it did not. Same presence convention as unfollow_lag_days:
   *  explicit `null` clears it to "unbounded", omitted leaves it unchanged. */
  backfill_lookback_months_sparse?: number | null
  /** The newest N interactions inside the lookback are read; older ones set
   *  the floor a later Deepen can go behind (1–20000). */
  backfill_max_interactions?: number
  /** Interactions/month that make a follower "frequent" (0–1000). */
  backfill_frequency_threshold_per_month?: number
}

export const updateInternalAgentType = async (
  accessToken: string | null,
  agentTypeId: string,
  fields: InternalAgentTypeFields
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.INTERNAL_AGENT_TYPE_UPDATE, { agentTypeId }),
    {
      method: API_ROUTES.CONFIG.INTERNAL_AGENT_TYPE_UPDATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

export interface InternalAgentTaskModelFields {
  agent_type_id?: string
  task_key?: string
  llm_model_key?: string
  /** Replaces the stored bag with the RPC-validated subset, so clearing a key
   *  genuinely removes it. Omit to leave the bag untouched. */
  params?: Record<string, unknown>
  enabled?: boolean
}

export const createInternalAgentTaskModel = async (
  accessToken: string | null,
  fields: InternalAgentTaskModelFields
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.INTERNAL_AGENT_TASK_MODEL_CREATE),
    {
      method: API_ROUTES.CONFIG.INTERNAL_AGENT_TASK_MODEL_CREATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

export const updateInternalAgentTaskModel = async (
  accessToken: string | null,
  taskModelId: string,
  fields: InternalAgentTaskModelFields
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.INTERNAL_AGENT_TASK_MODEL_UPDATE, {
      taskModelId
    }),
    {
      method: API_ROUTES.CONFIG.INTERNAL_AGENT_TASK_MODEL_UPDATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

export const deleteInternalAgentTaskModel = async (
  accessToken: string | null,
  taskModelId: string
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.INTERNAL_AGENT_TASK_MODEL_DELETE, {
      taskModelId
    }),
    {
      method: API_ROUTES.CONFIG.INTERNAL_AGENT_TASK_MODEL_DELETE.method,
      headers: authHeaders(accessToken)
    }
  )
  await readJson(response)
}

export interface LlmModelFields {
  model_key?: string
  provider?: string
  model_name?: string
  api_key_secret_name?: string
  input_cost_per_mtok?: number | null
  output_cost_per_mtok?: number | null
  max_output_tokens_default?: number | null
  enabled?: boolean
  description?: string
}

export const createLlmModel = async (
  accessToken: string | null,
  fields: LlmModelFields
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.LLM_MODEL_CREATE),
    {
      method: API_ROUTES.CONFIG.LLM_MODEL_CREATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

/** Disabling a model that is still routed comes back 409 with the count. */
export const updateLlmModel = async (
  accessToken: string | null,
  modelId: string,
  fields: LlmModelFields
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.LLM_MODEL_UPDATE, { modelId }),
    {
      method: API_ROUTES.CONFIG.LLM_MODEL_UPDATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

export const deleteLlmModel = async (
  accessToken: string | null,
  modelId: string
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.LLM_MODEL_DELETE, { modelId }),
    {
      method: API_ROUTES.CONFIG.LLM_MODEL_DELETE.method,
      headers: authHeaders(accessToken)
    }
  )
  await readJson(response)
}
